import * as React from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  DateTimeInput,
  NumberInput,
  required,
  ReferenceArrayInput,
  SelectArrayInput,
  SelectInput,
  FormDataConsumer,
  Toolbar,
  SaveButton,
} from "react-admin";
import { useClub } from "../club/ClubContext";

type Freq = "NONE" | "DAILY" | "WEEKLY" | "MONTHLY";

const NoDeleteToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

function buildRRule(freq: Freq, interval: number, byday: string[]) {
  if (freq === "NONE") return null;
  const parts = [`FREQ=${freq}`, `INTERVAL=${Math.max(1, interval || 1)}`];
  if (freq === "WEEKLY" && byday?.length) parts.push(`BYDAY=${byday.join(",")}`);
  return parts.join(";");
}

function normalizeUrl(url?: string) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function encodeNewlines(value?: string | null) {
  if (value == null) return value;
  return String(value)
    .replace(/\r\n/g, "\n")
    .replace(/\n/g, "\\n")
    .trim();
}

function decodeNewlines(value?: string | null) {
  return String(value ?? "")
    .replace(/\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .trim();
}

function parseRRule(rrule?: string | null): { freq: Freq; interval: number; byday: string[] } {
  if (!rrule) return { freq: "NONE", interval: 1, byday: [] };

  const parts = String(rrule)
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);

  const map: Record<string, string> = {};
  for (const p of parts) {
    const idx = p.indexOf("=");
    if (idx > 0) map[p.slice(0, idx).toUpperCase()] = p.slice(idx + 1);
  }

  const freqRaw = (map["FREQ"] ?? "NONE").toUpperCase();
  const freq: Freq =
    freqRaw === "DAILY" || freqRaw === "WEEKLY" || freqRaw === "MONTHLY" ? (freqRaw as Freq) : "NONE";

  const interval = Math.max(1, Number(map["INTERVAL"] ?? 1) || 1);
  const byday = (map["BYDAY"] ? map["BYDAY"].split(",") : []).map((x) => x.trim()).filter(Boolean);

  return { freq, interval, byday };
}

function minutesBetween(start: any, end: any): number | null {
  if (!start || !end) return null;
  const s = start instanceof Date ? start : new Date(start);
  const e = end instanceof Date ? end : new Date(end);
  const ms = e.getTime() - s.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 60000);
}

function addMinutes(dateValue: any, mins: number): Date | null {
  if (!dateValue || !Number.isFinite(mins)) return null;
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (!Number.isFinite(d.getTime())) return null;
  return new Date(d.getTime() + mins * 60000);
}

function formatDurationHint(mins?: number | null) {
  if (!mins || mins <= 0) return "";
  const days = Math.floor(mins / (24 * 60));
  const rest = mins % (24 * 60);
  const hours = Math.floor(rest / 60);
  const m = rest % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(" ");
}

function RecurrenceFields() {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        const freq: Freq = (formData?.recurrence_freq ?? "NONE") as Freq;

        return (
          <>
            <SelectInput
              source="recurrence_freq"
              label="Wiederholung"
              defaultValue="NONE"
              choices={[
                { id: "NONE", name: "Keine" },
                { id: "DAILY", name: "Täglich" },
                { id: "WEEKLY", name: "Wöchentlich" },
                { id: "MONTHLY", name: "Monatlich" },
              ]}
              helperText="Optional. Wenn du eine Wiederholung wählst, erzeugt das System automatisch Folgetermine."
              fullWidth
            />

            {freq !== "NONE" && (
              <>
                <NumberInput
                  source="recurrence_interval"
                  label="Intervall"
                  defaultValue={1}
                  helperText={
                    freq === "DAILY"
                      ? "Alle X Tage (1 = täglich, 2 = jeden 2. Tag, …)"
                      : freq === "WEEKLY"
                      ? "Alle X Wochen (1 = wöchentlich, 2 = alle 2 Wochen, …)"
                      : "Alle X Monate (1 = monatlich, 2 = alle 2 Monate, …)"
                  }
                />

                {freq === "WEEKLY" && (
                  <SelectArrayInput
                    source="recurrence_byday"
                    label="Wochentage"
                    choices={[
                      { id: "MO", name: "Mo" },
                      { id: "TU", name: "Di" },
                      { id: "WE", name: "Mi" },
                      { id: "TH", name: "Do" },
                      { id: "FR", name: "Fr" },
                      { id: "SA", name: "Sa" },
                      { id: "SU", name: "So" },
                    ]}
                    helperText="Wähle die Tage, an denen der Termin stattfinden soll."
                    fullWidth
                  />
                )}
              </>
            )}
          </>
        );
      }}
    </FormDataConsumer>
  );
}

export function EventSeriesEdit(props: any) {
  const { clubId } = useClub();

  return (
    <Edit
      {...props}
      transform={(data: any) => {
        const freq = (data.recurrence_freq ?? "NONE") as Freq;
        const interval = Number(data.recurrence_interval ?? 1);
        const byday = (data.recurrence_byday ?? []) as string[];

        const rrule = buildRRule(freq, interval, byday);
        const hasRepeat = freq !== "NONE";

        const teamIds: number[] = Array.isArray(data.team_ids)
          ? data.team_ids.map((x: any) => Number(x)).filter((x: any) => Number.isFinite(x))
          : [];

        const scope = teamIds.length > 0 ? "TEAMS" : "ALL_CLUB";

        // Ende (UI only) => duration_minutes für Einzeltermine
        let durationMinutes = Number(data.duration_minutes ?? 120);
        if (!hasRepeat && data.ends_at_ui && data.starts_at) {
          const m = minutesBetween(data.starts_at, data.ends_at_ui);
          if (m == null) {
            throw new Error("Ende muss nach dem Start liegen.");
          }
          durationMinutes = m;
        }

        return {
          ...data,

          club_id: clubId,
          scope,
          team_ids: scope === "TEAMS" ? teamIds : [],

          rrule,

          link_url: normalizeUrl(data.link_url),
          description: encodeNewlines(data.description),

          duration_minutes: durationMinutes,

          // UI-only Feld entfernen
          ends_at_ui: undefined,
        };
      }}
    >
      <SimpleForm
        toolbar={<NoDeleteToolbar />}
        defaultValues={(record: any) => {
          const parsed = parseRRule(record?.rrule);

          const hasRepeat = parsed.freq !== "NONE";
          const starts = record?.starts_at;
          const dur = Number(record?.duration_minutes ?? 0);

          // Für Einzeltermin: ends_at_ui vorfüllen (Start + Dauer), damit man es sofort sieht/ändern kann
          const endsAtUi = !hasRepeat && starts && dur > 0 ? addMinutes(starts, dur) : undefined;

          return {
            recurrence_freq: parsed.freq,
            recurrence_interval: parsed.interval,
            recurrence_byday: parsed.byday,

            ends_at_ui: endsAtUi,
          };
        }}
      >
        <TextInput
          source="title"
          label="Titel"
          validate={required()}
          helperText="Wird im Kalender und in der Termin-Detailansicht angezeigt."
          fullWidth
        />

        <TextInput
          source="description"
          label="Beschreibung"
          helperText="Optional. Zusätzliche Infos (z. B. Treffpunkt, Hinweise)."
          multiline
          minRows={3}
          fullWidth
          format={(v) => decodeNewlines(v)}
        />

        <TextInput
          source="link_url"
          label="Link"
          helperText="Optional. Z. B. WhatsApp-Gruppe, Google Maps, Webseite."
          fullWidth
        />

        <ReferenceArrayInput
          source="team_ids"
          reference="teams"
          label="Teams (optional)"
          filter={clubId ? { clubId } : {}}
          helperText="Leer lassen = alle im Verein. Wenn du Teams auswählst, sehen nur diese Teams den Termin."
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>

        <DateTimeInput
          source="starts_at"
          label="Start"
          validate={required()}
          helperText="Startdatum und Uhrzeit des ersten Termins (lokal in Europe/Berlin)."
        />

        <FormDataConsumer>
          {({ formData }) => {
            const freq: Freq = (formData?.recurrence_freq ?? "NONE") as Freq;
            const hasRepeat = freq !== "NONE";
            const showEnds = !hasRepeat;

            const minsFromEnds = minutesBetween(formData?.starts_at, formData?.ends_at_ui);

            return (
              <>
                <NumberInput
                  source="duration_minutes"
                  label="Dauer (Minuten)"
                  defaultValue={120}
                  helperText={
                    showEnds
                      ? "Einzeltermin: Entweder Dauer in Minuten ODER Endzeit angeben."
                      : "Wiederholung: Dauer max. 24h."
                  }
                  disabled={showEnds && !!formData?.ends_at_ui}
                />

                {showEnds && (
                  <DateTimeInput
                    source="ends_at_ui"
                    label="Ende (optional)"
                    helperText={
                      minsFromEnds
                        ? `Ergibt: ${minsFromEnds} Minuten (${formatDurationHint(minsFromEnds)})`
                        : "Für mehrtägige Einzeltermine (Trainingslager). Muss nach dem Start liegen."
                    }
                  />
                )}
              </>
            );
          }}
        </FormDataConsumer>

        <TextInput
          source="timezone"
          label="Zeitzone"
          defaultValue="Europe/Berlin"
          helperText="Standard: Europe/Berlin. Nur ändern, wenn der Termin wirklich in einer anderen Zeitzone stattfindet."
        />

        <TextInput
          source="location"
          label="Spielort"
          helperText="Ort/Adresse (erscheint als 'Ort' im Kalender). Optional."
          fullWidth
        />

        <DateTimeInput
          source="meeting_point_at"
          label="Treffpunkt-Zeit (optional)"
          helperText="Optional. Wann sich alle treffen sollen, falls abweichend vom Start. Wird bei Wiederholung anteilig auf jeden Termin übertragen."
        />

        <RecurrenceFields />

        <DateTimeInput
          source="repeat_until"
          label="Wiederholen bis (optional)"
          helperText="Optional. Wenn leer: Backend setzt automatisch ein sinnvolles Ende (z. B. Jahresende)."
        />
      </SimpleForm>
    </Edit>
  );
}
